/**
 * nebulaTextTokenizer.js
 *
 * P1–P7 of canvas text editing (Phase 4 #659).
 *
 * Walks an HTML source string, identifies text-bearing leaf elements,
 * injects `data-pid="N"` attributes on them, and builds a source-position
 * map so edits made in contenteditable mode can be patched back into
 * editableCode without string searching.
 *
 * "Text-bearing leaf" = an element from TEXT_TAGS whose innerHTML contains
 * no block-level or media child elements AND whose innerHTML range does not
 * contain another TEXT_TAGS element (P7 nested-element guard).
 * Inline markup (<strong>, <em>, <span>, etc.) is transparent.
 *
 * Returns:
 *   {
 *     taggedHtml  : string  — original HTML with data-pid injected on text leaves
 *     pidMap      : object  — { [pid]: { originalText, sourceStart, sourceEnd } }
 *                             positions reference the ORIGINAL html (= editableCode)
 *   }
 *
 * Patching on edit-mode exit (P5):
 *   Collect changed pids. Sort by sourceEnd descending so earlier patches
 *   don't shift later positions. For each changed pid:
 *     editableCode =
 *       editableCode.slice(0, sourceStart) + newText + editableCode.slice(sourceEnd)
 *   If a pid's source range is fully contained within another changed pid's
 *   source range, skip the inner one (the outer patch subsumes it).
 */

// Block-level and media elements that disqualify an element from being a
// text leaf when found inside its innerHTML.
const HAS_BLOCK_CHILD_RE =
  /<(div|section|article|header|footer|nav|main|aside|ul|ol|table|form|figure|blockquote|pre|h[1-6]|p|img|picture|video|audio|canvas|svg|iframe)\b/i;

// The set of tag names we attempt to tag as text leaves.
const TEXT_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p',
  'button', 'label',
  'figcaption', 'caption',
  'dt', 'dd',
  'td', 'th',
  'li',
  'a',
];

/**
 * tokenizeHtmlForEditing(html)
 *
 * Two-pass algorithm (P7):
 *  Pass 1 — collect all raw candidates from TEXT_TAGS.
 *  P7 filter — discard any candidate whose innerHTML range contains another
 *              candidate's opening-tag start (i.e. keep only innermost leaves).
 *  Pass 2 — assign pids in document order, build insertions + pidMap.
 *
 * @param {string} html  The raw HTML source (= editableCode for HTML artifacts).
 * @returns {{ taggedHtml: string, pidMap: Object }}
 */
export function tokenizeHtmlForEditing(html) {
  if (!html) return { taggedHtml: html ?? '', pidMap: {} };

  // ── Pass 1: collect raw candidates ──────────────────────────────────────────
  // Each entry: { openTagStart, openTagEnd, insertOffset, closingStart, textContent }
  const rawCandidates = [];
  // Track openTagStart values we've already seen to prevent duplicate entries
  // (a tag name can't appear in two different TEXT_TAGS iterations, but guard anyway).
  const seenStarts = new Set();

  for (const tagName of TEXT_TAGS) {
    // Matches an opening (non-self-closing) tag.
    // (?=[\s>]) lookahead prevents <li> matching <link>, <p> matching <pre>, etc.
    const openRe = new RegExp(`<(${tagName})(?=[\\s>])([^>]*)>`, 'gi');

    // Used for depth-counting to find the matching close tag.
    const depthRe = new RegExp(`<(/?)(${tagName})(?=[\\s>\\/])[^>]*>`, 'gi');

    let m;
    while ((m = openRe.exec(html)) !== null) {
      const openTagStart = m.index;
      if (seenStarts.has(openTagStart)) continue;

      const openTagEnd = openTagStart + m[0].length; // first char after '>'
      const insertOffset = openTagEnd - 1;           // position of the closing '>'

      // Depth scan to find matching close tag.
      let depth = 1;
      depthRe.lastIndex = openTagEnd;
      let closingStart = -1;
      let dm;
      while ((dm = depthRe.exec(html)) !== null) {
        if (dm[1] === '/') {
          if (--depth === 0) { closingStart = dm.index; break; }
        } else {
          depth++;
        }
      }
      if (closingStart === -1) continue; // unclosed — skip

      const innerHTML = html.slice(openTagEnd, closingStart);

      // Skip if block/media children are present.
      if (HAS_BLOCK_CHILD_RE.test(innerHTML)) continue;

      // Skip whitespace-only or empty elements.
      const textContent = innerHTML.replace(/<[^>]+>/g, '').trim();
      if (!textContent) continue;

      seenStarts.add(openTagStart);
      rawCandidates.push({ openTagStart, openTagEnd, insertOffset, closingStart, textContent });
    }
  }

  // ── P7: nested-element guard ─────────────────────────────────────────────────
  // Discard any candidate that CONTAINS another candidate within its innerHTML.
  // "Contains" = another candidate's openTagStart falls strictly inside
  // [outer.openTagEnd, outer.closingStart).
  // This ensures only the innermost text element in a nesting chain gets a pid,
  // preventing nested contentEditable conflicts in the browser.
  const leafCandidates = rawCandidates.filter((outer) =>
    !rawCandidates.some(
      (inner) =>
        inner !== outer &&
        inner.openTagStart >= outer.openTagEnd &&
        inner.openTagStart < outer.closingStart,
    ),
  );

  // ── Pass 2: assign pids in document order ────────────────────────────────────
  leafCandidates.sort((a, b) => a.openTagStart - b.openTagStart);

  const pidMap = {};
  const insertions = [];
  let pid = 0;

  for (const { openTagEnd, insertOffset, closingStart, textContent } of leafCandidates) {
    const currentPid = ++pid;
    pidMap[currentPid] = {
      originalText: textContent,
      // Positions in the ORIGINAL html string (used by P5 to patch editableCode).
      sourceStart: openTagEnd,
      sourceEnd: closingStart,
    };
    insertions.push({ offset: insertOffset, text: ` data-pid="${currentPid}"` });
  }

  // Apply insertions in reverse offset order so earlier offsets stay valid.
  insertions.sort((a, b) => b.offset - a.offset);

  let taggedHtml = html;
  for (const { offset, text } of insertions) {
    taggedHtml = taggedHtml.slice(0, offset) + text + taggedHtml.slice(offset);
  }

  return { taggedHtml, pidMap };
}

/**
 * buildPatchedHtml(editableCode, pidMap, changedPids)
 *
 * Convenience helper for P5 — applies text edits captured in contenteditable
 * mode back into the original HTML source string.
 *
 * @param {string} editableCode  Original HTML source (before tokenization).
 * @param {Object} pidMap        Map returned by tokenizeHtmlForEditing.
 * @param {Object} changedPids   { [pid]: newText } — only pids whose text changed.
 * @returns {string} Patched HTML source.
 */
export function buildPatchedHtml(editableCode, pidMap, changedPids) {
  const changedEntries = Object.entries(changedPids)
    .map(([pidStr, newText]) => {
      const pidNum = Number(pidStr);
      const entry = pidMap[pidNum];
      if (!entry) return null;
      return { pid: pidNum, newText, ...entry };
    })
    .filter(Boolean);

  if (changedEntries.length === 0) return editableCode;

  // Sort by sourceEnd descending so patching from the back keeps positions stable.
  changedEntries.sort((a, b) => b.sourceEnd - a.sourceEnd);

  // Drop inner-range entries that are fully contained within another changed entry.
  // This prevents double-patching when a parent and a child are both changed
  // (the parent patch already overwrites the child's range).
  const filtered = changedEntries.filter((entry, idx) => {
    return !changedEntries.some(
      (other, otherIdx) =>
        otherIdx !== idx &&
        other.sourceStart <= entry.sourceStart &&
        other.sourceEnd >= entry.sourceEnd,
    );
  });

  let result = editableCode;
  for (const { sourceStart, sourceEnd, newText } of filtered) {
    result =
      result.slice(0, sourceStart) +
      escapeHtml(newText) +
      result.slice(sourceEnd);
  }

  return result;
}

/**
 * Minimal HTML escape for user-typed text before injecting into source.
 * Only escapes characters that would break HTML structure.
 */
function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
